import { useEffect, useRef, type ReactNode } from 'react';
import { useT } from '../i18n';
import { CloseIcon } from './Icons';

/** Bottom sheet modal (phone pattern). Esc and the backdrop close it; focus moves inside. */
export function Sheet({ title, onClose, children }: { title: ReactNode; onClose: () => void; children: ReactNode }) {
  const t = useT();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className="safe-bottom relative max-h-[92dvh] w-full max-w-xl overflow-y-auto rounded-t-3xl bg-card px-4 pb-6 pt-3 outline-none sm:rounded-3xl"
      >
        <div className="mb-2 flex items-center gap-2">
          <h2 className="min-w-0 flex-1 text-[20px]">{title}</h2>
          <button type="button" className="inline-flex min-h-tap min-w-tap items-center justify-center rounded-xl" aria-label={t('common.close')} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
